import React from "react";
import PropTypes from "prop-types";
import { ThemeProvider as StyledThemeProvider, } from "styled-components";
import { createContext, useContext, useEffect, useMemo, useState } from "react";
import GlobalStyle from "./globalStyle";
import { ITheme } from "./ITheme";
import { theme as lightTheme, darkTheme } from "./theme";

export type ThemeMode = "light" | "dark";

const THEME_MODE_STORAGE_KEY = "herenow-theme-mode";

// Reads a previously-saved choice; falls back to the OS's prefers-color-scheme
// when nothing has been saved yet. Guarded for SSR/non-browser environments.
const getInitialThemeMode = (): ThemeMode => {
    if (typeof window === "undefined") {
        return "light";
    }

    const stored = window.localStorage?.getItem(THEME_MODE_STORAGE_KEY);
    if (stored === "light" || stored === "dark") {
        return stored;
    }

    const prefersDark = window.matchMedia?.("(prefers-color-scheme: dark)").matches;
    return prefersDark ? "dark" : "light";
};

type ThemeProviderProps = {
    children: React.ReactNode;
};

type ThemeModeContextValue = {
    mode: ThemeMode;
    toggleTheme: () => void;
    setThemeMode: (mode: ThemeMode) => void;
};

const ThemeContext = createContext<ITheme | undefined>(undefined);
const ThemeModeContext = createContext<ThemeModeContextValue | undefined>(undefined);

const ThemeProvider = ({ children }: ThemeProviderProps) => {
    const [mode, setMode] = useState<ThemeMode>(getInitialThemeMode);
    const resolvedTheme: ITheme = mode === "dark" ? darkTheme : lightTheme;

    useEffect(() => {
        window.localStorage?.setItem(THEME_MODE_STORAGE_KEY, mode);
        // Kept in sync for the small number of CSS rules (e.g. the data grid's
        // dark-mode row overrides) that are keyed off this attribute directly
        // rather than the generated --color-* variables.
        document.documentElement.setAttribute("data-bs-theme", mode);
    }, [mode]);

    const modeContextValue = useMemo<ThemeModeContextValue>(() => ({
        mode,
        toggleTheme: () => setMode((current) => (current === "dark" ? "light" : "dark")),
        setThemeMode: setMode,
    }), [mode]);

    return (
        <ThemeContext.Provider value={resolvedTheme}>
            <ThemeModeContext.Provider value={modeContextValue}>
                <StyledThemeProvider theme={resolvedTheme}>
                    <GlobalStyle theme={resolvedTheme} />
                    {children}
                </StyledThemeProvider>
            </ThemeModeContext.Provider>
        </ThemeContext.Provider>
    );
};

ThemeProvider.propTypes = {
    children: PropTypes.node.isRequired,
};

const useTheme = (): ITheme => {
    const theme = useContext(ThemeContext);
    if (theme === undefined) {
        throw new Error("useTheme must be used within a ThemeProvider");
    }
    return theme;
};

const useThemeMode = (): ThemeModeContextValue => {
    const modeContext = useContext(ThemeModeContext);
    if (modeContext === undefined) {
        throw new Error("useThemeMode must be used within a ThemeProvider");
    }
    return modeContext;
};

export { ThemeProvider, useTheme, useThemeMode };